import type { VariantProps } from 'class-variance-authority';

import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { buttonVariants } from './button-variants.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltip?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  isBusy?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, tooltip, tooltipPosition, isBusy = false, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isIconButton = variant?.startsWith('icon') || size?.startsWith('icon');
    const buttonProps = {
      className: cn(buttonVariants({ variant, size, className }), isBusy && 'cursor-not-allowed'),
      ref,
      ...props,
    };

    if (isBusy) {
      buttonProps.disabled = true;
    }

    const content = (
      <>
        <span
          className={cn(
            'inline-flex items-center justify-center gap-2',
            !['sidebar', 'icon', 'icon-outline'].includes(variant ?? '') && 'overflow-hidden',
            isBusy && 'opacity-0',
          )}
        >
          {children}
        </span>
        {isBusy && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            {isIconButton ? (
              <span
                data-loading-indicator="spinner"
                className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
              />
            ) : (
              <span data-loading-indicator="dots" className="flex items-center gap-1 mt-[1px]">
                <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce [animation-delay:0ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce [animation-delay:150ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce [animation-delay:300ms]" />
              </span>
            )}
          </span>
        )}
      </>
    );

    if (tooltip) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Comp {...buttonProps}>{content}</Comp>
            </TooltipTrigger>
            <TooltipContent side={tooltipPosition || 'top'} align="center" sideOffset={8}>
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <Comp {...buttonProps}>{content}</Comp>;
  },
);
Button.displayName = 'Button';

export { Button };
